import { type FC, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ChevronIcon from '@assets/img/icons/chevron.svg?react';
import cn from 'classnames';
import { transform } from 'framer-motion';

import { AppRoute } from '@/common/app/routes';
import { TickerInner } from '@/components/common/Ticker/TickerInner';
import { StringMain, TextSmall } from '@/components/Typography/Typography';
import { Picture } from '@/components/ui/Picture/Picture';
import { StarsPattern } from '@/components/ui/StarsPattern/StarsPattern';

import { ModalContext } from '../../Modal/config';

import css from './styles.module.scss';

import dicePng from '/img/dice-game/dice.png';
import diceWebp from '/img/dice-game/dice.webp';
import tgemPng from '/img/tgem-3d.png';
import tgemWebp from '/img/tgem-3d.webp';

interface TGemsDiceInfoProps {
  variant: 'tgem' | 'dice';
  showTicker?: boolean;
}

const TGemsDiceInfo: FC<TGemsDiceInfoProps> = ({ variant, showTicker }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { closeModal } = useContext(ModalContext);

  const calcEllipsePosition = transform([-200, 200], ['-100%', '100%'], {
    clamp: true,
  });
  const calcStarsPosition = transform([-252, 252], ['-100%', '100%']);

  const text = {
    tgem: {
      title: 'T-Gems',
      description: 'T-Gems fuel your growth!',
    },
    dice: {
      title: 'Roll Craft',
      description: 'Try your luck at the Roll Craft!',
    },
  };

  const image = {
    tgem: {
      png: tgemPng,
      webp: tgemWebp,
    },
    dice: {
      png: dicePng,
      webp: diceWebp,
    },
  };

  const ticker = {
    tgem: 'Dropping now',
    dice: 'Drop paused',
  };

  const accentBg = variant === 'tgem' ? 'bg-irrigo-purple' : 'bg-purple';

  const goTo = (): void => {
    if (variant === 'tgem') {
      navigate(AppRoute.SHOP, { state: { activeTab: '2' } });
      closeModal();
    }
  };

  return (
    <div onClick={goTo} className={css.tgemDiceInfo}>
      {showTicker && (
        <TickerInner
          className={cn(
            'absolute top-0 right-0 z-[2]',
            variant === 'tgem' ? 'text-accent' : 'text-white/60',
          )}
          variant="black-30-main"
          text="xs-medium"
          label={t(ticker[variant])}
        />
      )}
      <div className="flex items-center gap-1 relative z-[2]">
        <StringMain weight="bold" className="!leadind-1.2">
          {t(text[variant].title)}
        </StringMain>
        <ChevronIcon className="w-6 h-6 text-white" />
      </div>
      <TextSmall
        weight="regular"
        className="!text-[14px] !text-white/60 !leading-1.2 relative z-[2]"
      >
        {t(text[variant].description)}
      </TextSmall>
      <div className="flex items-center justify-center aspect-square absolute z-[2] top-1/2 -translate-y-1/2 w-[6.25rem] right-6">
        <Picture
          className={variant === 'tgem' ? 'w-[6.25rem]' : 'w-[4.5rem]'}
          img={image[variant].png}
          webp={image[variant].webp}
          alt={variant}
        />
      </div>

      <div
        style={{
          transform: `translateX(${calcEllipsePosition(-50)}) translateY(${calcEllipsePosition(-14)})`,
        }}
        className={cn(css.ellipseLeft, accentBg)}
      ></div>
      <div
        style={{
          transform: 'translateX(-10.22%) translateY(-46.59%)',
        }}
        className={cn(css.ellipseRight, accentBg)}
      ></div>
      <StarsPattern
        style={{
          transform: `translateY(${calcStarsPosition(-75)})`,
        }}
        className={css.starsPattern}
      />
    </div>
  );
};

export { TGemsDiceInfo };
